// Settings → Company Profile tab.
//
// Source-of-truth: /var/www/kitchenos/portal-standalone.html
//   tab button:           line 27035  (#settings-panel-company)
//   renderCompanyPanel:   line 27070
//   onboarding wizard:    line 26753  (rndCompany — same field set, used for
//                         pre-fill and validation on first run)
//
// Field set
// ---------
// The legacy portal stores company details in a single localStorage blob
// (CO_KEY) with the flat keys: businessName, address, phone, email,
// companyNo, vat, website. The master spec requires a richer address
// (line1 / line2 / city / county / postcode) and an extra `tradingName`
// field, so we expand the schema here and keep a server-side `address`
// object alongside the legacy flat string for backwards-compat with any
// reads that haven't been migrated.
//
// Backend
// -------
// There is NO `/api/portal/settings/company-profile` endpoint in
// kitchenos-api today (verified against server.js as of 2026-05). We:
//   1. Try `GET /api/portal/settings/company-profile` first; on 404/501
//      we fall back to an empty form (with a "Demo data — saved locally"
//      banner if anything is in sessionStorage from a previous edit).
//   2. On Save, POST to the same path. On 404/501 we persist to
//      sessionStorage under `tkos.set.companyProfile` so the form state
//      survives a tab switch in the demo.
//
//   TODO: GET  /api/portal/settings/company-profile  → CompanyProfile
//   TODO: POST /api/portal/settings/company-profile  ← CompanyProfile
//                                                    → { savedAt: number }

import { ApiError } from "@tkos/api-client";
import { iconMarkup } from "@tkos/shared-ui";
import type { SettingsTab } from "../types";
import type { PageContext } from "../../../shell/types";

import "./company-profile.css";
import { bankProblems, formatSortCode, serverBankProblem, serverErrorMessage, type BankProblem } from "./bank-details";

// ── Types ────────────────────────────────────────────────────────────────────

interface Address {
  line1: string;
  line2: string;
  city: string;
  county: string;
  postcode: string;
}

interface CompanyProfile {
  businessName: string;
  tradingName: string;
  address: Address;
  phone: string;
  email: string;
  website: string;
  /** Whether the business is VAT registered. Master switch for VAT on quotes:
   *  ON → quotes carry VAT; OFF → ex-VAT, no VAT line. The number is for the
   *  legally-required display on VAT documents. */
  vatRegistered: boolean;
  vatNumber: string;
  registrationNumber: string;
  /** Default cover/closing notes seeded into each new quote (editable per
   *  quote in the Quote Builder). */
  quoteIntroDefault: string;
  quoteClosingDefault: string;
  /** BACS details, shown to the customer on an ACCEPTED quote only (never on
   *  a speculative one — Steve, 2026-09-11). Purely display text: the platform
   *  does not process, reconcile or track a bank transfer. Card payments stay
   *  on Stripe, which is configured under Settings → Payments. */
  bankAccountName: string;
  bankSortCode: string;
  bankAccountNumber: string;
  bankPaymentReferenceNote: string;
  /** Number that inbound calls to the retailer's dedicated platform number are
   *  forwarded to. Defaults (server-side) to the owner mobile. */
  forwardTo: string;
}

interface ServerCompanyProfile extends Partial<Omit<CompanyProfile, "address">> {
  address?: Partial<Address> | string;
  /** Legacy flat-string address; we'll pour it into address.line1 if present. */
  addressString?: string;
  /** Epoch ms — last server-side save. */
  savedAt?: number;
}

interface SaveResponse {
  savedAt?: number;
}

// ── Constants ────────────────────────────────────────────────────────────────

const STORAGE_KEY = "tkos.set.companyProfile";

const EMPTY_PROFILE: CompanyProfile = {
  businessName: "",
  tradingName: "",
  address: { line1: "", line2: "", city: "", county: "", postcode: "" },
  phone: "",
  email: "",
  website: "",
  vatRegistered: false,
  vatNumber: "",
  registrationNumber: "",
  quoteIntroDefault: "",
  quoteClosingDefault: "",
  bankAccountName: "",
  bankSortCode: "",
  bankAccountNumber: "",
  bankPaymentReferenceNote: "",
  forwardTo: "",
};

const RELATIVE_TIME = new Intl.RelativeTimeFormat("en-GB", { numeric: "auto" });

// ── Helpers ──────────────────────────────────────────────────────────────────

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

function isMissingEndpoint(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 404 || err.status === 501);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function readSession(): CompanyProfile | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CompanyProfile>;
    return mergeProfile(EMPTY_PROFILE, parsed);
  } catch {
    return null;
  }
}

function writeSession(profile: CompanyProfile): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
  } catch {
    /* ignore quota errors — non-fatal */
  }
}

function mergeProfile(
  base: CompanyProfile,
  patch: Partial<CompanyProfile> | undefined,
): CompanyProfile {
  if (!patch) return { ...base, address: { ...base.address } };
  return {
    businessName: patch.businessName ?? base.businessName,
    tradingName: patch.tradingName ?? base.tradingName,
    address: { ...base.address, ...(patch.address ?? {}) },
    phone: patch.phone ?? base.phone,
    email: patch.email ?? base.email,
    website: patch.website ?? base.website,
    vatRegistered: patch.vatRegistered ?? base.vatRegistered,
    vatNumber: patch.vatNumber ?? base.vatNumber,
    registrationNumber: patch.registrationNumber ?? base.registrationNumber,
    quoteIntroDefault: patch.quoteIntroDefault ?? base.quoteIntroDefault,
    quoteClosingDefault: patch.quoteClosingDefault ?? base.quoteClosingDefault,
    bankAccountName: patch.bankAccountName ?? base.bankAccountName,
    bankSortCode: patch.bankSortCode ?? base.bankSortCode,
    bankAccountNumber: patch.bankAccountNumber ?? base.bankAccountNumber,
    bankPaymentReferenceNote:
      patch.bankPaymentReferenceNote ?? base.bankPaymentReferenceNote,
    forwardTo: patch.forwardTo ?? base.forwardTo,
  };
}

function fromServer(s: ServerCompanyProfile): CompanyProfile {
  // Tolerate both the legacy flat-string address and a structured one.
  let address: Address;
  if (typeof s.address === "string") {
    address = { ...EMPTY_PROFILE.address, line1: s.address };
  } else if (s.address && typeof s.address === "object") {
    address = { ...EMPTY_PROFILE.address, ...s.address };
  } else if (typeof s.addressString === "string") {
    address = { ...EMPTY_PROFILE.address, line1: s.addressString };
  } else {
    address = { ...EMPTY_PROFILE.address };
  }
  return {
    businessName: s.businessName ?? "",
    tradingName: s.tradingName ?? "",
    address,
    phone: s.phone ?? "",
    email: s.email ?? "",
    website: s.website ?? "",
    vatRegistered: !!s.vatRegistered,
    vatNumber: s.vatNumber ?? "",
    registrationNumber: s.registrationNumber ?? "",
    quoteIntroDefault: s.quoteIntroDefault ?? "",
    quoteClosingDefault: s.quoteClosingDefault ?? "",
    bankAccountName: s.bankAccountName ?? "",
    bankSortCode: s.bankSortCode ?? "",
    bankAccountNumber: s.bankAccountNumber ?? "",
    bankPaymentReferenceNote: s.bankPaymentReferenceNote ?? "",
    forwardTo: s.forwardTo ?? "",
  };
}

function relativeTime(epoch: number): string {
  const diffMs = epoch - Date.now();
  const diffSec = Math.round(diffMs / 1000);
  const abs = Math.abs(diffSec);
  if (abs < 60) return RELATIVE_TIME.format(diffSec, "second");
  if (abs < 3600) return RELATIVE_TIME.format(Math.round(diffSec / 60), "minute");
  if (abs < 86400) return RELATIVE_TIME.format(Math.round(diffSec / 3600), "hour");
  return RELATIVE_TIME.format(Math.round(diffSec / 86400), "day");
}

function validate(p: CompanyProfile): string[] {
  const errors: string[] = [];
  if (!p.businessName.trim()) errors.push("Business name is required");
  if (!p.address.line1.trim()) errors.push("Address line 1 is required");
  if (!p.address.city.trim()) errors.push("Town/city is required");
  if (!p.address.postcode.trim()) errors.push("Postcode is required");
  if (!p.phone.trim()) errors.push("Phone is required");
  if (!p.email.trim()) {
    errors.push("Email is required");
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.email.trim())) {
    errors.push("Email looks invalid");
  }
  return errors;
}

// ── Render ───────────────────────────────────────────────────────────────────

function renderShell(container: HTMLElement): {
  bannerEl: HTMLElement;
  formEl: HTMLFormElement;
  savedAtEl: HTMLElement;
  saveBtn: HTMLButtonElement;
  errorsEl: HTMLElement;
} {
  container.innerHTML = `
    <section class="set-co-root" aria-labelledby="set-co-title">
      <header class="set-co-head">
        <div>
          <h2 class="set-section-title" id="set-co-title">Business details</h2>
          <p class="set-co-sub">These details appear on quotes, invoices and throughout your portal.</p>
        </div>
        <div class="set-co-saved" data-role="saved-at" aria-live="polite"></div>
      </header>

      <div class="set-co-banner" data-role="banner" hidden></div>

      <form class="set-co-form" data-role="form" novalidate>
        <div class="set-row">
          <div class="set-field">
            <label for="set-co-businessName">Business name <span class="set-co-req">*</span></label>
            <input class="set-input" type="text" id="set-co-businessName" name="businessName" autocomplete="organization" required>
          </div>
          <div class="set-field">
            <label for="set-co-tradingName">Trading name <span class="set-co-opt">(optional)</span></label>
            <input class="set-input" type="text" id="set-co-tradingName" name="tradingName" placeholder="If different from business name">
          </div>
        </div>

        <h3 class="set-co-subhead">Registered address</h3>
        <div class="set-field">
          <label for="set-co-line1">Address line 1 <span class="set-co-req">*</span></label>
          <input class="set-input" type="text" id="set-co-line1" name="line1" autocomplete="address-line1" required>
        </div>
        <div class="set-field">
          <label for="set-co-line2">Address line 2 <span class="set-co-opt">(optional)</span></label>
          <input class="set-input" type="text" id="set-co-line2" name="line2" autocomplete="address-line2">
        </div>
        <div class="set-row">
          <div class="set-field">
            <label for="set-co-city">Town / city <span class="set-co-req">*</span></label>
            <input class="set-input" type="text" id="set-co-city" name="city" autocomplete="address-level2" required>
          </div>
          <div class="set-field">
            <label for="set-co-county">County <span class="set-co-opt">(optional)</span></label>
            <input class="set-input" type="text" id="set-co-county" name="county" autocomplete="address-level1">
          </div>
          <div class="set-field set-co-postcode">
            <label for="set-co-postcode">Postcode <span class="set-co-req">*</span></label>
            <input class="set-input" type="text" id="set-co-postcode" name="postcode" autocomplete="postal-code" required>
          </div>
        </div>

        <h3 class="set-co-subhead">Contact</h3>
        <div class="set-row">
          <div class="set-field">
            <label for="set-co-phone">Phone <span class="set-co-req">*</span></label>
            <input class="set-input" type="tel" id="set-co-phone" name="phone" autocomplete="tel" required>
          </div>
          <div class="set-field">
            <label for="set-co-email">Email <span class="set-co-req">*</span></label>
            <input class="set-input" type="email" id="set-co-email" name="email" autocomplete="email" required>
          </div>
          <div class="set-field">
            <label for="set-co-website">Website</label>
            <input class="set-input" type="url" id="set-co-website" name="website" placeholder="https://example.co.uk">
          </div>
        </div>
        <div class="set-row">
          <div class="set-field" style="grid-column:1/-1">
            <label for="set-co-forward">Forward calls to</label>
            <input class="set-input" type="tel" id="set-co-forward" name="forwardTo" autocomplete="tel" placeholder="+44 7…">
            <p style="margin:6px 0 0;font-size:12px;color:var(--color-text-muted,#94a3b8);line-height:1.45">
              Calls to your dedicated platform number are forwarded to this number. Defaults to your mobile — set it to wherever you want calls to ring.
            </p>
          </div>
        </div>

        <h3 class="set-co-subhead">VAT &amp; identifiers</h3>
        <div class="set-row">
          <div class="set-field" style="grid-column:1/-1">
            <label class="set-co-switch" for="set-co-vatreg">
              <input type="checkbox" id="set-co-vatreg" name="vatRegistered">
              <span>My business is VAT registered</span>
            </label>
            <p style="margin:6px 0 0;font-size:12px;color:var(--color-text-muted,#94a3b8);line-height:1.45">
              When on, quotes include VAT. When off, quotes are produced ex-VAT with no VAT line. You can still adjust the VAT rate on an individual quote.
            </p>
          </div>
        </div>
        <div class="set-row">
          <div class="set-field">
            <label for="set-co-vat">VAT number</label>
            <input class="set-input" type="text" id="set-co-vat" name="vatNumber" placeholder="GB 123 4567 89">
            <p class="set-co-vat-warn" data-role="vat-warn" hidden
               style="margin:6px 0 0;font-size:12px;color:#e58200;line-height:1.45">
              You're marked as VAT registered but haven't entered a VAT number — it's legally required on VAT quotes and invoices.
            </p>
          </div>
          <div class="set-field">
            <label for="set-co-reg">Registration number</label>
            <input class="set-input" type="text" id="set-co-reg" name="registrationNumber" placeholder="09876543">
          </div>
        </div>

        <h3 class="set-co-subhead">Quote notes</h3>
        <p style="margin:-4px 0 10px;font-size:12px;color:var(--color-text-muted,#94a3b8);line-height:1.45">
          Default cover and closing notes added to every new quote. You can edit them per quote in the Quote Builder.
        </p>
        <div class="set-field">
          <label for="set-co-quote-intro">Quote introduction / cover note</label>
          <textarea class="set-input" id="set-co-quote-intro" name="quoteIntroDefault" rows="3" maxlength="4000"></textarea>
        </div>
        <div class="set-field">
          <label for="set-co-quote-closing">Quote closing note</label>
          <textarea class="set-input" id="set-co-quote-closing" name="quoteClosingDefault" rows="3" maxlength="4000"></textarea>
        </div>

        <h3 class="set-co-subhead">Bank details for BACS payments</h3>
        <p style="margin:-4px 0 10px;font-size:12px;color:var(--color-text-muted,#94a3b8);line-height:1.45">
          Shown to the customer <strong>only once they've accepted a quote</strong> &mdash; never on a quote they're still considering. Appears on the accepted quote PDF and in their portal. Card payments are handled separately under Settings &rarr; Payments.
        </p>
        <div class="set-row">
          <div class="set-field" style="grid-column:1/-1">
            <label for="set-co-bank-name">Account name</label>
            <input class="set-input" type="text" id="set-co-bank-name" name="bankAccountName"
                   maxlength="200" autocomplete="off" placeholder="The name on your business account">
          </div>
        </div>
        <div class="set-row">
          <div class="set-field">
            <label for="set-co-bank-sort">Sort code <span class="set-co-bank-hint">6 digits</span></label>
            <input class="set-input" type="text" id="set-co-bank-sort" name="bankSortCode"
                   maxlength="20" inputmode="numeric" autocomplete="off" placeholder="12-34-56"
                   aria-describedby="set-co-bank-sort-err">
            <p class="set-co-bank-err" id="set-co-bank-sort-err" data-role="bank-sort-err" hidden></p>
          </div>
          <div class="set-field">
            <label for="set-co-bank-acct">Account number <span class="set-co-bank-hint">usually 8 digits</span></label>
            <input class="set-input" type="text" id="set-co-bank-acct" name="bankAccountNumber"
                   maxlength="40" inputmode="numeric" autocomplete="off" placeholder="12345678"
                   aria-describedby="set-co-bank-acct-err">
            <p class="set-co-bank-err" id="set-co-bank-acct-err" data-role="bank-acct-err" hidden></p>
          </div>
        </div>
        <div class="set-field">
          <label for="set-co-bank-ref">Payment reference note <span style="font-weight:400;color:var(--color-text-muted,#94a3b8)">(optional)</span></label>
          <input class="set-input" type="text" id="set-co-bank-ref" name="bankPaymentReferenceNote"
                 maxlength="300" placeholder="Please use your quote number as the payment reference">
        </div>
        <div class="set-co-bank-preview" data-role="bank-preview" hidden>
          <div class="set-co-bank-preview-lbl">Customers will see</div>
          <div data-role="bank-preview-body"></div>
        </div>
        <p class="set-co-bank-warn" data-role="bank-warn" hidden
           style="margin:6px 0 0;font-size:12px;color:#e58200;line-height:1.45">
          Enter both an account name and an account number, or leave all the bank fields blank &mdash; partial details won't be shown to customers.
        </p>

        <ul class="set-co-errors" data-role="errors" hidden></ul>

        <div class="set-co-actions">
          <button type="submit" class="set-btn" data-role="save">Save</button>
        </div>
      </form>
    </section>
  `;

  return {
    bannerEl: container.querySelector<HTMLElement>("[data-role='banner']")!,
    formEl: container.querySelector<HTMLFormElement>("[data-role='form']")!,
    savedAtEl: container.querySelector<HTMLElement>("[data-role='saved-at']")!,
    saveBtn: container.querySelector<HTMLButtonElement>("[data-role='save']")!,
    errorsEl: container.querySelector<HTMLElement>("[data-role='errors']")!,
  };
}

function fillForm(form: HTMLFormElement, p: CompanyProfile): void {
  const set = (name: string, value: string): void => {
    const el = form.elements.namedItem(name);
    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) el.value = value;
  };
  set("businessName", p.businessName);
  set("tradingName", p.tradingName);
  set("line1", p.address.line1);
  set("line2", p.address.line2);
  set("city", p.address.city);
  set("county", p.address.county);
  set("postcode", p.address.postcode);
  set("phone", p.phone);
  set("email", p.email);
  set("website", p.website);
  set("vatNumber", p.vatNumber);
  set("registrationNumber", p.registrationNumber);
  set("quoteIntroDefault", p.quoteIntroDefault);
  set("quoteClosingDefault", p.quoteClosingDefault);
  set("bankAccountName", p.bankAccountName);
  set("bankSortCode", p.bankSortCode);
  set("bankAccountNumber", p.bankAccountNumber);
  set("bankPaymentReferenceNote", p.bankPaymentReferenceNote);
  set("forwardTo", p.forwardTo);
  const vatReg = form.elements.namedItem("vatRegistered");
  if (vatReg instanceof HTMLInputElement) vatReg.checked = p.vatRegistered;
}

function readForm(form: HTMLFormElement): CompanyProfile {
  const get = (name: string): string => {
    const el = form.elements.namedItem(name);
    return (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) ? el.value.trim() : "";
  };
  const getBool = (name: string): boolean => {
    const el = form.elements.namedItem(name);
    return el instanceof HTMLInputElement ? el.checked : false;
  };
  return {
    businessName: get("businessName"),
    tradingName: get("tradingName"),
    address: {
      line1: get("line1"),
      line2: get("line2"),
      city: get("city"),
      county: get("county"),
      postcode: get("postcode"),
    },
    phone: get("phone"),
    email: get("email"),
    website: get("website"),
    vatRegistered: getBool("vatRegistered"),
    vatNumber: get("vatNumber"),
    registrationNumber: get("registrationNumber"),
    bankAccountName: get("bankAccountName"),
    bankSortCode: get("bankSortCode"),
    bankAccountNumber: get("bankAccountNumber"),
    bankPaymentReferenceNote: get("bankPaymentReferenceNote"),
    quoteIntroDefault: get("quoteIntroDefault"),
    quoteClosingDefault: get("quoteClosingDefault"),
    forwardTo: get("forwardTo"),
  };
}

function renderSavedAt(el: HTMLElement, savedAt: number | null, isLocal: boolean): void {
  if (savedAt === null) {
    el.textContent = "";
    el.removeAttribute("data-state");
    return;
  }
  const rel = relativeTime(savedAt);
  el.textContent = isLocal ? `Last saved ${rel} (locally)` : `Last saved ${rel}`;
  el.dataset.state = isLocal ? "local" : "server";
}

function renderErrors(el: HTMLElement, errors: string[]): void {
  if (errors.length === 0) {
    el.hidden = true;
    el.innerHTML = "";
    return;
  }
  el.hidden = false;
  el.innerHTML = errors.map((e) => `<li>${escapeHtml(e)}</li>`).join("");
}

function renderBanner(el: HTMLElement, msg: string | null, kind: "info" | "error"): void {
  if (!msg) {
    el.hidden = true;
    el.textContent = "";
    el.classList.remove("set-co-banner--error");
    return;
  }
  el.hidden = false;
  el.textContent = msg;
  el.classList.toggle("set-co-banner--error", kind === "error");
}

// ── Mount ────────────────────────────────────────────────────────────────────

const tab: SettingsTab = {
  id: "company-profile",
  label: "Company Profile",
  icon: iconMarkup("kitchen", { size: "sm" }),

  mount(container: HTMLElement, ctx: PageContext) {
    const { bannerEl, formEl, savedAtEl, saveBtn, errorsEl } = renderShell(container);

    // Show the "registered but no number" nudge whenever the toggle is on and
    // the VAT number is blank (decision: toggle is master, number is for the
    // legally-required display on documents).
    const vatWarnEl = container.querySelector<HTMLElement>("[data-role='vat-warn']");
    const updateVatWarn = (): void => {
      if (!vatWarnEl) return;
      const reg = formEl.elements.namedItem("vatRegistered");
      const num = formEl.elements.namedItem("vatNumber");
      const on = reg instanceof HTMLInputElement && reg.checked;
      const blank = num instanceof HTMLInputElement && num.value.trim() === "";
      vatWarnEl.hidden = !(on && blank);
    };
    // Nudge when the bank block is half-filled. The render sites require BOTH
    // an account name and an account number (a sort code alone isn't payable),
    // so partial details silently show the customer nothing — this says so
    // rather than letting the retailer believe they're published.
    const bankWarnEl = container.querySelector<HTMLElement>("[data-role='bank-warn']");
    const updateBankWarn = (): void => {
      if (!bankWarnEl) return;
      const val = (name: string): string => {
        const el = formEl.elements.namedItem(name);
        return el instanceof HTMLInputElement ? el.value.trim() : "";
      };
      const name = val("bankAccountName");
      const acct = val("bankAccountNumber");
      const sort = val("bankSortCode");
      const ref = val("bankPaymentReferenceNote");
      const anyFilled = !!(name || acct || sort || ref);
      const payable = !!(name && acct);
      bankWarnEl.hidden = !(anyFilled && !payable);
      showBankProblems(bankProblems(sort, acct, val("vatNumber")));
      // What an accepted customer will read, labelled exactly as on their PDF.
      if (bankPreviewEl && bankPreviewBodyEl) {
        bankPreviewEl.hidden = !payable;
        bankPreviewBodyEl.textContent = "";
        for (const [k, v] of [["Account name", name], ["Sort code", formatSortCode(sort)], ["Account number", acct]] as const) {
          if (!v) continue;
          const row = document.createElement("div");
          row.textContent = `${k}: ${v}`;
          bankPreviewBodyEl.appendChild(row);
        }
      }
    };
    const bankPreviewEl = container.querySelector<HTMLElement>("[data-role='bank-preview']");
    const bankPreviewBodyEl = container.querySelector<HTMLElement>("[data-role='bank-preview-body']");
    const bankErrEls: Record<BankProblem["field"], HTMLElement | null> = {
      bankSortCode: container.querySelector<HTMLElement>("[data-role='bank-sort-err']"),
      bankAccountNumber: container.querySelector<HTMLElement>("[data-role='bank-acct-err']"),
    };
    const showBankProblems = (problems: BankProblem[]): void => {
      for (const field of ["bankSortCode", "bankAccountNumber"] as const) {
        const el = bankErrEls[field];
        const input = formEl.elements.namedItem(field);
        const p = problems.find((x) => x.field === field);
        if (el) { el.hidden = !p; el.textContent = p ? p.message : ""; }
        if (input instanceof HTMLInputElement) input.setAttribute("aria-invalid", p ? "true" : "false");
      }
    };
    // Tidy a typed sort code to 12-34-56 when the field is left.
    const sortInput = formEl.elements.namedItem("bankSortCode");
    if (sortInput instanceof HTMLInputElement) {
      sortInput.addEventListener("blur", () => { sortInput.value = formatSortCode(sortInput.value); updateBankWarn(); });
    }

    const onFormInput = (): void => {
      updateVatWarn();
      updateBankWarn();
    };
    formEl.addEventListener("input", onFormInput);

    let controller: AbortController | null = new AbortController();
    let disposed = false;
    let savedAt: number | null = null;
    let savedAtIsLocal = false;
    let savedAtTimer: number | null = null;

    const refreshSavedAtLabel = (): void => {
      renderSavedAt(savedAtEl, savedAt, savedAtIsLocal);
    };

    const scheduleSavedAtRefresh = (): void => {
      if (savedAtTimer !== null) {
        window.clearInterval(savedAtTimer);
      }
      savedAtTimer = window.setInterval(refreshSavedAtLabel, 30_000);
    };

    const setBusy = (busy: boolean): void => {
      saveBtn.disabled = busy;
      saveBtn.textContent = busy ? "Saving…" : "Save";
    };

    // ── Initial load ────────────────────────────────────────────────────────
    const load = async (): Promise<void> => {
      try {
        const data = await ctx.api<ServerCompanyProfile>(
          "/portal/settings/company-profile",
          { signal: controller!.signal },
        );
        if (disposed) return;
        const profile = fromServer(data);
        fillForm(formEl, profile);
        if (typeof data.savedAt === "number") {
          savedAt = data.savedAt;
          savedAtIsLocal = false;
          refreshSavedAtLabel();
          scheduleSavedAtRefresh();
        }
        renderBanner(bannerEl, null, "info");
      } catch (err) {
        if (isAbortError(err) || disposed) return;
        if (isMissingEndpoint(err)) {
          // Endpoint not yet implemented — fall back to sessionStorage so the
          // form still feels alive between tab switches in the demo.
          const cached = readSession();
          if (cached) {
            fillForm(formEl, cached);
            savedAt = Date.now();
            savedAtIsLocal = true;
            refreshSavedAtLabel();
            scheduleSavedAtRefresh();
            renderBanner(
              bannerEl,
              "Server endpoint pending — your edits are kept locally for this session.",
              "info",
            );
          } else {
            fillForm(formEl, EMPTY_PROFILE);
            renderBanner(
              bannerEl,
              "Server endpoint pending — saves will be kept locally for now.",
              "info",
            );
          }
          return;
        }
        const msg = err instanceof Error ? err.message : "Couldn't load company profile";
        renderBanner(bannerEl, msg, "error");
        ctx.showToast(msg, "error");
      }
    };

    void load().then(() => { if (!disposed) { updateVatWarn(); updateBankWarn(); } });

    // ── Save handler ────────────────────────────────────────────────────────
    const onSubmit = async (e: SubmitEvent): Promise<void> => {
      e.preventDefault();
      const profile = readForm(formEl);
      const bankIssues = bankProblems(profile.bankSortCode, profile.bankAccountNumber, profile.vatNumber);
      const errors = [...validate(profile), ...bankIssues.map((b) => b.message)];
      showBankProblems(bankIssues);
      renderErrors(errorsEl, errors);
      if (errors.length > 0) {
        ctx.showToast("Please fix the highlighted fields", "error");
        return;
      }

      setBusy(true);

      // Always write through to session immediately so a tab switch mid-save
      // doesn't lose data.
      writeSession(profile);

      try {
        controller?.abort();
        controller = new AbortController();
        const res = await ctx.api<SaveResponse>("/portal/settings/company-profile", {
          method: "POST",
          body: JSON.stringify(profile),
          signal: controller.signal,
        });
        if (disposed) return;
        savedAt = typeof res.savedAt === "number" ? res.savedAt : Date.now();
        savedAtIsLocal = false;
        refreshSavedAtLabel();
        scheduleSavedAtRefresh();
        renderBanner(bannerEl, null, "info");
        ctx.showToast("Company profile saved", "success");
      } catch (err) {
        if (isAbortError(err) || disposed) return;
        if (isMissingEndpoint(err)) {
          savedAt = Date.now();
          savedAtIsLocal = true;
          refreshSavedAtLabel();
          scheduleSavedAtRefresh();
          renderBanner(
            bannerEl,
            "Server endpoint pending — saved locally for this session.",
            "info",
          );
          ctx.showToast("Saved locally — server endpoint pending", "info");
        } else {
          const msg = err instanceof Error ? serverErrorMessage(err.message) : "Save failed";
          // A bank-detail refusal goes beside its own box, like the form's own checks.
          const bankIssue = err instanceof Error ? serverBankProblem(err.message) : null;
          if (bankIssue) showBankProblems([bankIssue]);
          renderBanner(bannerEl, msg, "error");
          ctx.showToast(msg, "error");
        }
      } finally {
        if (!disposed) setBusy(false);
      }
    };

    formEl.addEventListener("submit", onSubmit);

    return () => {
      disposed = true;
      controller?.abort();
      controller = null;
      formEl.removeEventListener("submit", onSubmit);
      formEl.removeEventListener("input", onFormInput);
      if (savedAtTimer !== null) {
        window.clearInterval(savedAtTimer);
        savedAtTimer = null;
      }
    };
  },
};

export default tab;
